// SEN Yoga Frome - Service Page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Heart, Shield, Users } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import { usePageMeta, generateServiceSchema, SchemaScript } from "../../components/shared/SEOHelper";

const SENYogaFrome = () => {
  usePageMeta({
    title: "SEN Yoga Frome | Special Educational Needs Yoga Somerset",
    description: "Specialist yoga for children and young people with special educational needs in Frome, Somerset. Gentle, adaptive classes tailored to each individual with",
    canonical: "https://annelielily.com/yoga-classes/sen-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "SEN Yoga in Frome",
    description: "Specialist yoga classes for children and young people with special educational needs in Frome, Somerset.",
    serviceType: "SEN Yoga Class",
    url: "https://annelielily.com/yoga-classes/sen-yoga"
  });

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-28 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              SEN Yoga in Frome
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              SEN Yoga
            </h1>
            <p className="text-gold text-lg mb-6">Specialist Yoga for Special Educational Needs</p>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              Annelie has extensive experience working with children and young people with special educational needs. These gentle, adaptive classes are carefully tailored to each individual, creating a safe and inclusive environment where every child can experience the benefits of yoga at their own pace.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">
              Enquire About SEN Yoga <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              A Tailored Approach
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Every child is unique, and SEN yoga sessions are designed with this in mind. Annelie works closely with parents and carers to understand each child's needs, abilities, and what helps them feel safe and comfortable.
              </p>
              <p>
                Sessions may include gentle movement, breathing exercises, sensory activities, and relaxation techniques — all adapted to suit the individual child.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              What Makes These Sessions Special
            </h2>
            <div className="space-y-4">
              {[
                { icon: Heart, title: "Individually Tailored", desc: "Each session is carefully designed around the child's specific needs and abilities" },
                { icon: Shield, title: "Safe & Inclusive", desc: "A calm, supportive environment where every child feels welcome and secure" },
                { icon: Users, title: "Experienced Teacher", desc: "Annelie has extensive experience working with children with special educational needs" },
                { icon: Check, title: "Own Pace", desc: "No pressure or expectations — children experience yoga in whatever way works for them" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-white/70 p-5 rounded-xl border border-gold/10">
                  <item.icon className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading text-lg text-neutral-800 mb-1">{item.title}</h3>
                    <p className="text-neutral-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Interested in SEN Yoga?
            </h2>
            <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
              Get in touch to discuss your child's needs. Annelie is happy to have an initial conversation to understand how she can best support your child.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="bottom-cta">
              Get in Touch <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/yoga-classes/children-family-yoga" className="text-sm text-gold hover:underline">Children & Family Yoga</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes/private-yoga" className="text-sm text-gold hover:underline">Private Sessions</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/yoga-classes" className="text-sm text-gold hover:underline">Adult Yoga Classes</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SENYogaFrome;
